import { autoWebViewJs } from "@droid/android"
import { IProject } from "@droid/project-v2-interface"
import { projectUtils } from "@droid/project-v2-ext"
import { Log } from "@fanfanlo"
import { assembleRhinoRunner, WebViewJsInjectKeys } from "@app/static"
import { user } from "@app/model"

const fileLog = new Log(false, 'runProject_f')
// fileLog.pause = true;  // 注释掉：允许日志输出
// fileLog.childrenPaused = true;  // 注释掉：允许子日志输出
/**
 * 运行项目
 * @param projectData 项目数据
 * @returns 执行结果
 */
export async function runProject(projectData: IProject) {
    const fnLog = fileLog.sub(false, 'runProject_fn')
    // fnLog.pause = true;  // 注释掉：允许日志输出
    // fnLog.childrenPaused = true;  // 注释掉：允许子日志输出
    fnLog.log('[runProject] 开始运行项目')
    fnLog.log('[runProject] projectData:', JSON.stringify(projectData, null, 2))

    // ==================== 清理空的 TriggerAction ====================
    fnLog.log('[runProject] 清理空的 TriggerAction')
    const removedCount = projectUtils.cleanEmptyTriggerActions(projectData)
    fnLog.log('[runProject] 清理了', removedCount, '个空 TriggerAction')

    if (removedCount > 0) {
        fnLog.log('[runProject] 清理后的 projectData:', JSON.stringify(projectData, null, 2))
    } else {
        fnLog.log('[runProject] 没有空的 TriggerAction 需要清理')
    }
    // ==================== 清理逻辑结束 ====================

    try {
        // 1. 获取项目所需的权限列表
        fnLog.log('[runProject] 获取权限列表')
        const permissions = await projectUtils.getProjectPermissionsData(projectData)
        fnLog.log('[runProject] 权限数量:', permissions.length)

        // 2. 获取项目所需的注入配置
        fnLog.log('[runProject] 获取注入配置', JSON.stringify(projectData))
        const projectInjects = projectUtils.getProjectInjects(projectData)
        fnLog.log('[runProject] 项目injects数量:', projectInjects.length)
        fnLog.log('[runProject] 项目injects内容:', projectInjects)

        // 3. 合并硬编码的 console 和项目中的 injects
        fnLog.log('[runProject] 合并注入配置')
        const finalInjects: (typeof projectInjects[0])[] = [
            [WebViewJsInjectKeys.console, 'console']
        ]
        fnLog.log('[runProject] 添加硬编码console inject')

        fnLog.log('[runProject] 遍历项目injects')
        for (let i = 0; i < projectInjects.length; i++) {
            const inject = projectInjects[i]
            const isInjectNull = inject === null
            const isInjectUndefined = inject === undefined
            fnLog.log('[runProject] 处理inject[', i, ']:', inject)
            fnLog.log('[runProject] inject是否为null:', isInjectNull)
            fnLog.log('[runProject] inject是否为undefined:', isInjectUndefined)

            if (isInjectNull) {
                fnLog.log('[runProject] 警告: inject[', i, '] 为null, 跳过')
                continue
            }

            if (isInjectUndefined) {
                fnLog.log('[runProject] 警告: inject[', i, '] 为undefined, 跳过')
                continue
            }

            finalInjects.push(inject)
        }

        fnLog.log('[runProject] 最终injects数量:', finalInjects.length)
        fnLog.log('[runProject] 最终injects内容:', JSON.stringify(finalInjects))

        fnLog.log('[runProject] 检查finalInjects中的每个元素')
        for (let i = 0; i < finalInjects.length; i++) {
            const inject = finalInjects[i]
            const isNull = inject === null
            const isUndefined = inject === undefined
            const isArray = Array.isArray(inject)
            fnLog.log('[runProject] finalInjects[', i, '] 是否为null:', isNull)
            fnLog.log('[runProject] finalInjects[', i, '] 是否为undefined:', isUndefined)
            fnLog.log('[runProject] finalInjects[', i, '] 是否为数组:', isArray)

            if (isArray) {
                const arrayLength = inject.length
                fnLog.log('[runProject] finalInjects[', i, '] 数组长度:', arrayLength)

                if (arrayLength > 0) {
                    fnLog.log('[runProject] finalInjects[', i, '][0]:', inject[0])
                } else {
                    fnLog.log('[runProject] finalInjects[', i, '] 是空数组')
                }
            } else {
                fnLog.log('[runProject] finalInjects[', i, '] 内容:', inject)
            }
        }

        // 4. 组装 RhinoRunner 配置
        fnLog.log('[runProject] 组装RhinoRunner配置')
        const conf = assembleRhinoRunner(projectData, permissions, {
            foregroundText: `正在执行: ${projectData.name}`,
            onlyRunSingle: true,
            stopPrevIfOnlyRunSingle: true,
            forceStopTime: 0,
            injects: finalInjects
        })
        fnLog.log('[runProject] RhinoRunner配置组装完成')
        fnLog.log('[runProject] 脚本长度:', conf.script.length)

        // 5. 调用 Android 端执行
        fnLog.log('[runProject] 调用autoWebViewJs.runProject', JSON.stringify(conf, null, 2))

        const userId = user.data.storeData.user_token || '';
        fnLog.log('[runProject] userId:', userId)

        const projectName = projectData.name || 'unnamed_project';
        const projectUlid = projectData.ulid;
        fnLog.log('[runProject] projectName:', projectName)
        fnLog.log('[runProject] projectUlid:', projectUlid)

        const result = await autoWebViewJs.runProject(conf, userId, projectName, projectUlid)
        fnLog.log('[runProject] 执行结果:', result)

        const hasJavaResultString = result.javaResultString ? true : false
        fnLog.log('[runProject] 是否有javaResultString:', hasJavaResultString)

        if (result.javaResultString) {
            fnLog.log('[runProject] 解析javaResultString')
            const parsedResult = JSON.parse(result.javaResultString)
            fnLog.log('[runProject] 解析后的结果:', parsedResult)

            const isSuccess = parsedResult.success ? true : false
            fnLog.log('[runProject] 是否成功:', isSuccess)

            if (isSuccess) {
                fnLog.log('[runProject] 项目执行成功')
            } else {
                fnLog.log('[runProject] 项目执行失败')
                fnLog.log('[runProject] 失败消息:', parsedResult.message)
            }
            return parsedResult
        } else {
            fnLog.log('[runProject] 没有返回数据')
            return null
        }
    } catch (error) {
        fnLog.log('[runProject] 执行异常:', error)
        throw error
    }
}
